// Angular-Module
import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {AbstractControl, UntypedFormArray, UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators} from '@angular/forms';

// ReactiveX for JavaScript
import {merge, Subject, Subscription} from 'rxjs';
import {filter, takeUntil} from 'rxjs/operators';
// Eigener Service
import {OrderFormService} from '../../order-form.service';
// Interfaces für Structured Objects einbinden
import {StorageService} from '@global/services/storage.service';
import {FormStatus} from '@shared/form-status';
import {LooseObject} from '@shared/loose-object';
import {SelectData} from '@shared/select-data';
import {hasOwn, notEmpty, notEmptyObject, numberRange, toSum} from '@shared/utils';
import moment from 'moment';
import {InitService} from '@global/services/init.service';
import {ListentriesService} from '@global/services/listentries.service';
import {Discount} from '@shared/discount';
import {compareDiscountRules, findBestRuleForQuantity} from '@shared/utils/discount-rules';
import {isValidNumber} from '@shared/utils/numbers';
import {InstitutionsService} from '@modules/institutions/institutions.service';
import {EmployeeSampleBatches, SampleBatchesService} from '@shared/samples/sample-batches.service';
// Environment einbinden
import {environment} from '@environment';

/**
 * Aggregierte Daten für eine Rabattgruppe
 * Enthält alle Produkte der Gruppe sowie die summierte Bestellmenge der Gruppe
 */
interface GroupAggregateCtrl {
    productCtrls: UntypedFormGroup[];
    qty: number;
}

/**
 * Map für Gruppenaggregate:
 * Schlüssel = groupId, Wert = Aggregatdaten der Gruppe
 */
type GroupAggregatesMapCtrl = Map<number, GroupAggregateCtrl>;

@Component({
    selector: 'phscw-order-form-items',
    templateUrl: './order-form-items.component.html',
    styleUrls: ['./order-form-items.component.scss'],
    standalone: false,
})
export class OrderFormItemsComponent implements OnInit, OnDestroy {
    // Wird bei ngOnDestroy ausgelöst um Observables-Subscription zu stoppen
    #componentDestroyed$ = new Subject<void>();

    gridId = 'OrderFormItems';

    // id of selected order
    orderId: number = null;

    // Zwischenspeichern der Auftragsdaten
    orderDataCopy: LooseObject;

    // data of selected order
    @Input() set orderData(order) {
        // Prüfe ob orderData nicht null/undefined oder empty object ist
        if (!(order && Object.keys(order).length === 0 && Object.getPrototypeOf(order) === Object.prototype)) {
            this.orderDataCopy = Object.assign(order);
            // Hole orderItems wenn es sich nicht um eine Neuanlage handelt
            if (order.id !== 0 && order.id !== null && typeof order.id !== 'undefined') {
                this.getOrderItems(order);
            }
        }
    }

    // defines if component is currently loading data
    loading = false;
    // defines if data is currently edited
    @Input() editMode = false;

    // Status des Formulars
    status: FormStatus;

    // Reactive forms
    orderItemFormGroup: UntypedFormGroup;

    // Event wird ausgelöst, wenn an den Item-Feldern Änderungen vorgenommen werden
    @Output() valueChanges = new EventEmitter<any>();

    // Sammlung der Subscriptions für Order-Item-Felder, die beim Wechsel der Auftragsart abbestellt werden müssen
    productItemFieldsSubscriptions = new Subscription();

    // Ausgewählte Auftragsart
    selectedOrderType: string;
    // Ausgewählte Auftragssortiment
    selectedOrderRange: number | string;
    // gewählte Produktgruppe
    selectedParentProductId: number | string;

    // Auswahl der Produktegruppen
    selectDataProductGroups: SelectData[] = [];
    // Auswahl der Sortimente
    selectDataProductRange: SelectData[] = [];
    // Auswahl der Produkt-Chargen
    selectDataProductBatches: SelectData[] = [];

    // PDEV-1683: auswaehlbare Chargen fuer den Mitarbeiter des Auftrags, null = (noch) nicht geladen
    #employeeSampleBatches: EmployeeSampleBatches = null;
    // PDEV-1683: Chargen werden gerade geladen - bis dahin keine Chargen zur Auswahl anbieten
    #employeeSampleBatchesLoading = false;
    // PDEV-1683: Mitarbeiter, fuer den die Chargen geladen wurden/werden
    #employeeSampleBatchesEmployeeId: number = null;
    /*
     * PDEV-1683: true = Bestandsueberschreitung sperrt die Menge (Validator), false = nur rote
     * Markierung und Rueckfrage vor dem Speichern (cw.frontend.blockSaveOnNegativeStock)
     */
    blockSaveOnNegativeStock = true;

    // Auswahl der Produkttypen für den Filter
    selectDataProductTypes: SelectData[] = [];
    selectDataSecondaryDiscountFactor: SelectData[] = [];
    selectDataSecondaryDiscountReason: SelectData[] = [];
    selectDataAmountFreeReason: SelectData[] = [];

    // Aktuell gewählter Produkttyp im Filter
    selectedProductType: string | null = null;

    // Gesamte Produktliste aus dem Storage
    orderEntryProductList = [];
    // Gesamte Produktgruppenliste aus dem Storage
    productGroupList = [];
    // Gesamte Sortimentsliste aus dem Storage
    productRangeList = [];
    // Gesamte Produktchargenliste aus dem Storage
    productBatchList = [];

    // Suchwert der Suchleiste bei der Produktliste
    productSearchValue: string;
    // Flag ob Produkt Suche aktiv ist
    productSearchActive = false;
    // Felder in denen gesucht werden soll
    productDataFieldsForSearch = ['product_id', 'product_label', 'product_erp_number', 'pzn'];

    // aktuell aktive Lieferdaten zur Anzeige in der Items-Tabelle
    deliveryDates: Date[];
    visibleDeliveryDates = 1;
    // maximale kostenlose Menge
    productsFreeSampleMaxAmount = 5;

    // Ob From initialisert ist
    init = false;

    // Zahlenformat für Preise
    customCurrencyFormat = {
        decimal_separator: ',',
        thousands_separator: '.',
        decimal_places: 2,
    };

    // Spaltendefinitionen für Grid Artikeltabelle
    articleGridColumns = [
        // {columnDef: 'product_id', header: 'Produkt-ID.', cell: (element: any) => `${element.product_id}`,  formatTemplate: 'flush_right'},
        {
            columnDef: 'label',
            header: 'Bezeichnung',
            cell: (element: any) => `${element.label}`,
        },
        {
            columnDef: 'delivery_address',
            header: 'Lieferaddresse',
            cell: (element: any) => `${element.delivery_address}`,
            formatWidth: '80px',
        },
        {
            columnDef: 'delivery_date',
            header: 'Lieferdatum',
            formatTemplate: 'date',
            cell: (element: any) => `${element.delivery_date}`,
        },
        {
            columnDef: 'order_item_delivery_institution_id',
            header: 'Empfänger',
            cell: (element: any) => `${element.delivery_institution_id}`,
        },
        {
            columnDef: 'package_size',
            header: 'Packungs-Gr.',
            cell: (element: any) => `${element.package_size}`,
        },
        {
            columnDef: 'quantity_unit',
            columnField: 'quantity_unit',
            header: 'VPE',
            cell: (element: any) => `${element.quantity_unit}`,
            listentry: 'productPackageUnit',
            formatTemplate: 'listentries',
        },
        {
            columnDef: 'pzn',
            header: ' PZN',
            cell: (element: any) => `${element.pzn || ''}`,
        },
        {
            columnDef: 'order_item_charge',
            header: 'Charge',
            cell: (element: any) => `${element.charge}`,
        },
        {
            columnDef: 'amount',
            header: 'Menge',
            cell: (element: any) => `${element.amount}`,
            formatTemplate: 'flush_right',
            formatWidth: '80px',
        },
        {
            columnDef: 'order_item_amount_free',
            header: 'Menge kostenlos',
            cell: (element: any) => `${element.amount_free || 0}`,
            formatTemplate: 'flush_right',
            formatWidth: '80px',
        },
        {
            columnDef: 'unit_price',
            header: 'Einzelpreis',
            cell: (element: any) => `${element.unit_price}`,
            formatTemplate: 'currency',
            formatWidth: '80px',
        },
        {
            columnDef: 'item_price',
            header: 'Positionspreis',
            cell: (element: any) => `${element.item_price}`,
            formatTemplate: 'currency',
            formatWidth: '80px',
        },
        {
            columnDef: 'discount',
            header: 'Rabatt',
            cell: (element: any) => `${element.discount}`,
            formatTemplate: 'percent',
            formatWidth: '80px',
        },
        {
            columnDef: 'order_item_secondary_discount',
            header: 'Sekundärrabatt',
            cell: (element: any) => `${element.secondary_discount || 0}`,
            formatTemplate: 'percent',
            formatWidth: '80px',
        },
        {
            columnDef: 'max_discount_text',
            header: '',
            cell: (element: any) => `${element.max_discount_text}`,
            formatWidth: '5px',
        },
        {
            columnDef: 'total_price',
            header: 'Gesamtpreis',
            cell: (element: any) => `${element.total_price}`,
            formatTemplate: 'currency',
            formatWidth: '80px',
        },
    ];

    readonly availableArticleGridDisplayedColumns = [
        'label',
        'delivery_date',
        'order_item_delivery_institution_id',
        'amount',
        'order_item_amount_free',
        'unit_price',
        'discount',
        'order_item_secondary_discount',
        'total_price',
        'order_item_charge',
    ];

    // per default show all available columns, will later be modified according to access-rights
    articleGridDisplayedColumns = [...this.availableArticleGridDisplayedColumns];

    articleData: LooseObject[] = [];
    articleDataReadOnlyView: LooseObject[] = [];

    // defines if data is currently edited
    discounts: Record<string, Discount[]> = {};

    /**
     * Freischaltungen für Sekundärrabatt / kostenlose Menge je "Sortiment|Produkt-ID" der gewählten Auftragsart,
     * werden aus orderEntryProductList (Preis-Datensatz) abgeleitet und bei Wechsel von Liste/Auftragsart neu aufgebaut
     */
    private orderFlags = new Map<string, {secondaryDiscount: boolean; freeAmount: boolean; freeAmountMax: number | null}>();
    private orderFlagsSource: {productList: unknown; orderType: string} = {productList: null, orderType: null};
    /**
     * PDEV-1775: im Auftragskopf uebernommener Rabatt (discountToBeApplied); ersetzt den Standardrabatt
     * des Auftrags (order_discount), solange er gesetzt ist.
     */
    private standardDiscountOverride: number | null = null;

    /**
     * Map von product_id zu den originalen Rabatt-Werten
     */
    private originalDiscountValues = new Map<
        number,
        {
            discount: number | null;
            flat_discount: number | null;
            special_price: number | null;
            valuta_date: Date | null;
        }
    >();

    /*
     * Flag, um die initialen Rabattwerte zu setzen, wenn keine anwendbare Rabattregel vorhanden ist
     * z.B. wenn Rabatte manuell eingegeben wurden & keine Rabattregel aktiv ist
     */
    private isInitialDiscountLoad = true;

    // Indexe aller Lieferdaten
    readonly deliveryDateIndexes: number[] = [];

    // Flag, ob die Duplikation von Auftragspositionen möglich ist
    duplicationOrderItemsEnabled = false;

    // Flag, ob beim Duplizieren Menge, K-Menge, S-Rabatt und Gründe übernommen werden (sonst leer)
    duplicationOrderItemsCopyValues = false;

    // Flag, ob der button zum zurücksetzen einer Position aktiv ist
    orderItemRowResetEnabled = false;

    // Subscriptions für duplizierte Produkt-Zeilen
    private productRowSubscriptions = new Map<UntypedFormGroup, Subscription>();

    /**
     * Konstruktor (inkl. dependency injection)
     * @param orderFormService
     * @param storageService
     */
    constructor(
        public orderFormService: OrderFormService,
        private storageService: StorageService,
        private initService: InitService,
        private listentriesService: ListentriesService,
        private institutionsService: InstitutionsService,
        private sampleBatchesService: SampleBatchesService,
    ) {}

    /**
     * Initialize
     */
    ngOnInit() {
        // Laden der benötigen listentry
        this.initService.allInitialized.pipe(takeUntil(this.#componentDestroyed$)).subscribe((result: boolean) => {
            this.loadListentries();
        });
        this.loadListentries();

        // PDEV-1683: config ob Bestandsueberschreitung sperrt (default) oder nur warnt
        this.storageService.getItem('config|blockSaveOnNegativeStock').then((value) => {
            this.blockSaveOnNegativeStock = value !== false;
        });

        // Leere Default-FormGroup anlegen, wird beim Laden der Produkte befüllt
        this.orderItemFormGroup = new UntypedFormGroup({});

        // Produktdaten initialisieren
        this.getOrderEntryProductList();

        // Events subscriben
        this.initializeEventSubscriptions();
        // Discounts laden
        this.orderFormService.getDiscounts(this.institutionsService.selectedInstitution.id).subscribe((result) => {
            this.discounts = result['data'];

            // falls das Formular schon initialisiert und befüllt ist, wende Gruppenrabatte an
            if (this.orderItemFormGroup && this.articleData) {
                this.updateAllDiscountGroups();
            }
        });

        // Alle Daten beim Login wurden in den Storage geladen
        this.initService.allInitialized.pipe(takeUntil(this.#componentDestroyed$)).subscribe((result: boolean) => {
            if (result) {
                this.loadFrontendConfiguration();
            }
        });

        this.loadFrontendConfiguration();
    }

    /**
     * Cleanup
     */
    ngOnDestroy() {
        this.#componentDestroyed$.next();
        this.#componentDestroyed$.complete();
    }

    /**
     * Susbcribe to events
     */
    private initializeEventSubscriptions(): void {
        // auf neu evaluierte Access-Rights reagieren
        this.orderFormService.currentOrderAccessRightsExplicit
            .pipe(takeUntil(this.#componentDestroyed$))
            .subscribe((currentRights) => {
                // sichtbare Spalten neu ermitteln - ausblenden, wenn in den Access-Rights explizit unsichtbar gesetzt
                this.articleGridDisplayedColumns = this.availableArticleGridDisplayedColumns.filter(
                    (columnKey) =>
                        typeof currentRights[columnKey] === 'undefined' || currentRights[columnKey]['ngIf'] === true,
                );

                // set required validator in reactive forms
                for (const [key, value] of Object.entries(this.orderFormService.orderAccessRightsExplicit)) {
                    if (!Object.prototype.hasOwnProperty.call(this.orderItemFormGroup.controls, key)) {
                        continue;
                    }

                    if (value['required']) {
                        this.orderItemFormGroup.controls[key].setValidators([Validators.required]);
                    } else {
                        this.orderItemFormGroup.controls[key].setValidators(null);
                    }
                    this.orderItemFormGroup.controls[key].updateValueAndValidity();
                }
            });

        // Auf das Event hören, wenn OrderType gewechselt wird
        this.orderFormService.selectedOrderType
            .pipe(
                takeUntil(this.#componentDestroyed$),
                /*
                 * Nur bei Neuanlage, sonst werden bereits geladene Daten im subscribe zurückgesetzt
                 * Bei existierndem Auftrag kann der OrderType sowieso nicht geändert werden
                 */
                filter(() => this.orderDataCopy?.id === 0),
            )
            .subscribe((event: string) => {
                // Werte intern speichern und Formular neu initialisieren
                this.selectedOrderType = event;
                this.selectDataProductGroups = [];
                this.selectDataProductRange = [];
                this.selectParentProductId(-1);
                // für den Order-Typ verfügbare Produkte laden, baut Items-Formular auf
                this.getOrderEntryProductList();
            });

        // Änderungen an der Lieferdaten-Konfiguration beobachten
        this.orderFormService.deliveryDatesChanged
            .pipe(takeUntil(this.#componentDestroyed$))
            .subscribe((event: Date[]) => {
                this.onChangedDeliveryDates(event);
            });

        // Auf das Event hören, wenn gesetzter Rabatt vom Auftragskopf übernommen werden soll
        this.orderFormService.discountToBeApplied
            .pipe(takeUntil(this.#componentDestroyed$))
            .subscribe((discountNew: number) => {
                this.applyDiscount(discountNew);
            });
    }

    /**
     * @brief Lade die Frontend-Konfiguration
     * @return {void}
     */
    loadFrontendConfiguration(): void {
        /*
         * Orders konfiguration laden
         * Hier können mehrere Konfigurationswerte geladen werden
         */
        this.storageService.getItem('config|orders').then((config: LooseObject) => {
            this.duplicationOrderItemsEnabled = config?.duplicationOrderItemsEnabled ?? false;
            this.duplicationOrderItemsCopyValues = config?.duplicationOrderItemsCopyValues ?? false;
            this.orderItemRowResetEnabled = config?.orderItemRowResetEnabled ?? false;
        });

        this.storageService.getItem('config|orders').then((config: LooseObject) => {
            this.productsFreeSampleMaxAmount = config?.productsFreeSampleMaxAmount ?? 5;
        });
    }

    /**
     * @brief Hole alle bestellten Produkte einer Order
     * @param {LooseObject} order Gesamte Bestelldaten
     */
    private getOrderItems(order: LooseObject) {
        // flag ob gerade geladen wird
        this.loading = true;

        // Zwischenspeichern der gesetzten Auftragsart
        this.selectedOrderType = order['order_type'];

        // Zwischenspeichern von order_items
        this.articleData = order['order_items'];

        this.articleDataReadOnlyView = order['orderItemsReadonlyView'];

        // zur Sicherheit die Anzahl sichtbarer Datumsspalten anpassen, sollte schon beim Laden geschehen sein
        this.visibleDeliveryDates = order.deliveryDates.length;
        this.rebuildDeliveryDateIndexes();

        const promise = this.storageService.getItem('orderEntryProductList');
        promise.then((productList: LooseObject) => {
            // Produkte Zwischenspeichern
            this.orderEntryProductList = productList['products'];
            this.productGroupList = productList['product_groups'];
            this.productRangeList = productList['product_range'];
            this.productBatchList = productList['product_batches'];

            // Auswahlfelder initialisieren
            this.getProductRangeForSelectData();
            this.getProductGroupsForSelectData();
            this.loadEmployeeSampleBatches();
            this.getProductBatchesForSelectData();

            // vorherige Subscriptions beenden, wenn neue Formularfelder erstellt werden
            this.productItemFieldsSubscriptions.unsubscribe();
            this.productItemFieldsSubscriptions = new Subscription();

            // Form zurücksetzen und neu aufbauen
            this.orderItemFormGroup.reset();
            this.orderItemFormGroup = this.initForm();
            this.productItemFieldsSubscriptions.add(
                this.orderItemFormGroup.valueChanges.subscribe((result) => {
                    // geänderte Daten nach außen geben
                    this.valueChanges.emit(result);
                }),
            );

            // Vorhandene Daten in Form aufnehmen
            for (const orderItem of this.articleData) {
                const productId = Number(orderItem.product_id);
                const recipientId = Number(orderItem.delivery_institution_id);
                const dupPos = Number(orderItem.duplicate_position ?? 0);

                let patched = false;

                for (const product of this.orderItemFormGroup.controls.productsFormArray['controls']) {
                    const pid = Number(product.get('product_id')?.value);
                    const rid = Number(product.get('order_item_delivery_institution_id')?.value);

                    /*
                     * Daten der Zeile aktualisieren, die zu Produkt und Lieferempfänger des OrderItems passt
                     * Nur die Originalzeile (duplicate_position === 0)
                     */
                    if (pid === productId && rid === recipientId && dupPos === 0) {
                        product.patchValue(
                            {
                                amount: orderItem.amount,
                                unit_price: orderItem.unit_price,
                                discount: orderItem.discount,
                                special_price: orderItem.special_price ?? null,
                                flat_discount: orderItem.flat_discount ?? null,
                                charge: orderItem.charge,
                                total_price: orderItem.total_price,
                                amountPerDeliveryDate: orderItem.amountPerDeliveryDate ?? [],
                                amount_free: orderItem.amount_free,
                                secondary_discount: orderItem.secondary_discount,
                                reason_secondary_discount: orderItem.reason_secondary_discount,
                                reason_amount_free: orderItem.reason_amount_free,
                                valuta_date: orderItem.valuta_date ?? null,
                                is_duplicate: false,
                                duplicate_position: 0,
                            },
                            {emitEvent: false},
                        );

                        // Map befüllen mit den initialen Rabatten
                        this.originalDiscountValues.set(productId, {
                            discount: orderItem.discount as number,
                            flat_discount: orderItem.flat_discount ?? null,
                            special_price: orderItem.special_price ?? null,
                            valuta_date: orderItem.valuta_date ?? null,
                        });

                        patched = true;
                        break;
                    }
                }

                // Falls es ein Duplikat ist (dupPos > 0), erzeuge die zusätzliche Zeile unterhalb der Originalzeile
                if (!patched && dupPos > 0) {
                    // Originalzeile suchen (duplicate_position === 0)
                    const fa = this.orderItemFormGroup.get('productsFormArray') as UntypedFormArray;
                    const baseIndex = (fa.controls as UntypedFormGroup[]).findIndex(
                        (fg) =>
                            Number(fg.get('product_id')?.value) === productId &&
                            Number(fg.get('order_item_delivery_institution_id')?.value) === recipientId &&
                            Number(fg.get('duplicate_position')?.value ?? 0) === 0,
                    );

                    // Originalzeile gefunden
                    if (baseIndex !== -1) {
                        const base = fa.at(baseIndex) as UntypedFormGroup;

                        // Neue Duplikat-Zeile aus dem Original erzeugen
                        const duplicate = this.buildDuplicateFromSource(base, dupPos);
                        duplicate.get('is_duplicate')?.setValue(true, {emitEvent: false});
                        duplicate.get('duplicate_position')?.setValue(dupPos, {emitEvent: false});

                        // Am Ende des Blocks einfügen
                        const insertAfter = this.findLastSiblingIndex(productId, recipientId);
                        const insertAt = insertAfter >= 0 ? insertAfter + 1 : baseIndex + 1;
                        fa.insert(insertAt, duplicate);

                        // Listener für die Duplikat-Zeile anlegen (Menge, Rabatt, etc.)
                        this.attachProductSubscriptions(duplicate);

                        // Werte patchen
                        duplicate.patchValue(
                            {
                                amount: orderItem.amount,
                                unit_price: orderItem.unit_price,
                                discount: orderItem.discount,
                                special_price: orderItem.special_price ?? null,
                                flat_discount: orderItem.flat_discount ?? null,
                                charge: orderItem.charge,
                                total_price: orderItem.total_price,
                                amountPerDeliveryDate: orderItem.amountPerDeliveryDate ?? [],
                                amount_free: orderItem.amount_free,
                                secondary_discount: orderItem.secondary_discount,
                                reason_secondary_discount: orderItem.reason_secondary_discount,
                                reason_amount_free: orderItem.reason_amount_free,
                                valuta_date: orderItem.valuta_date ?? null,
                                is_duplicate: true,
                                duplicate_position: dupPos,
                            },
                            {emitEvent: false},
                        );

                        // Map befüllen mit den initialen Rabatten
                        this.originalDiscountValues.set(productId, {
                            discount: orderItem.discount as number,
                            flat_discount: orderItem.flat_discount ?? null,
                            special_price: orderItem.special_price ?? null,
                            valuta_date: orderItem.valuta_date ?? null,
                        });
                    }
                }
            }

            // flag loading zurücksetzen
            this.loading = false;
        });
    }

    /**
     * Hole alle Produkte aus dem Storage
     */
    private getOrderEntryProductList() {
        // flag ob gerade geladen wird
        this.loading = true;

        // early return wenn keine Auftragsart gesetzt ist oder existierender Auftrag ausgewählt ist
        if (typeof this.selectedOrderType === 'undefined' || this.orderDataCopy?.id > 0) {
            this.loading = false;
            return;
        }

        const promise = this.storageService.getItem('orderEntryProductList');
        promise.then((productList: LooseObject) => {
            // Produkte Zwischenspeichern
            this.orderEntryProductList = productList['products'];
            this.productGroupList = productList['product_groups'];
            this.productRangeList = productList['product_range'];
            this.productBatchList = productList['product_batches'];

            // Auswahlfelder initialisieren
            this.getProductRangeForSelectData();
            this.getProductGroupsForSelectData();
            this.loadEmployeeSampleBatches();
            this.getProductBatchesForSelectData();

            // vorherige Subscriptions beenden, wenn neue Formularfelder erstellt werden
            this.productItemFieldsSubscriptions.unsubscribe();
            this.productItemFieldsSubscriptions = new Subscription();

            // Form zurücksetzen und neu aufbauen
            this.orderItemFormGroup.reset();
            this.orderItemFormGroup = this.initForm();
            this.productItemFieldsSubscriptions.add(
                this.orderItemFormGroup.valueChanges.subscribe((result) => {
                    // geänderte Daten nach außen geben
                    this.valueChanges.emit(result);
                }),
            );

            // flag loading zurücksetzen
            this.loading = false;
        });
    }

    /**
     * Bereite die verfügbaren Sortimente für Auswahlliste vor
     */
    private getProductRangeForSelectData() {
        // Default Auswahl "Alle" hinzufügen
        this.selectDataProductRange.push({
            id: 'all',
            label: 'Alle',
        });

        // Falls keine produkte exisiteren, keine anzeigen
        if (!this.checkIfProductsExits()) {
            return;
        }

        // Auswahlmöglichkeiten je nach verfügbaren Sortimenten hinzufügen
        for (const [key] of Object.entries(this.orderEntryProductList[this.selectedOrderType])) {
            this.selectDataProductRange.push({
                id: key,
                label: this.productRangeList[key].list_value,
            });
        }
        // Beim selektieren standardmäßig alle auswählen
        this.selectedOrderRange = 'all';
    }

    /**
     * Bereite die verfügbaren Produktgruppen für Auswahlliste vor
     */
    private getProductGroupsForSelectData() {
        // Default Auswahl "Alle" hinzufügen
        this.selectDataProductGroups.push({
            id: 'all',
            label: 'Alle',
        });

        // Auswahl für Produkte mit amount > 0 hinzufügen
        this.selectDataProductGroups.push({
            id: 'amountSet',
            label: 'Bestellung',
        });

        // Falls keine produkte exisiteren, keine anzeigen
        if (!this.checkIfProductsExits()) {
            return;
        }

        // Auswahlmöglichkeiten je nach verfügbarer Produktgruppe des momentan ausgewählten Sortiments hinzufügen
        for (const orderRange of Object.values(this.orderEntryProductList[this.selectedOrderType])) {
            for (const [key] of Object.entries(orderRange)) {
                // Keine Produkt-ID, nächsten Eintrag prüfen
                if (!key) {
                    continue;
                }

                // Hole Produktgruppe auf der höchsten Ebene des Produkts
                const selectDataEntry = this.getTopLevelProductGroup(key);

                // Prüfe ob Produktgruppe nicht bereits hinzugefügt worden ist
                const existingIndex = this.selectDataProductGroups.findIndex((item) => item.id === selectDataEntry.id);
                if (existingIndex === -1) {
                    this.selectDataProductGroups.push(selectDataEntry);
                }
            }
        }

        // Sortiere Liste alphabetisch nach label
        this.selectDataProductGroups = this.sortSelectDataListAlphabetically(this.selectDataProductGroups);

        // Beim selektieren standardmäßig alle auswählen
        this.selectedParentProductId = 'all';
    }

    /*
     * Prüfe ob Produkt eine Produktgruppe auf der Höchsten Ebene ist
     */
    private isTopLevelProductGroup(productId: string): boolean {
        return this.productGroupList[productId]?.parent_id === null;
    }

    /*
     * Hole Produktgruppe der höchsten Ebene des Produkts
     */
    private getTopLevelProductGroup(productId: string) {
        if (this.isTopLevelProductGroup(productId)) {
            return {
                id: productId,
                label: this.productGroupList[productId]?.label,
            };
        }

        return this.getTopLevelProductGroup(this.productGroupList[productId]?.parent_id);
    }

    /*
     * Sortiere die SelectData Liste alphabetisch nach "label"
     */
    private sortSelectDataListAlphabetically(list) {
        // Ignore die ersten 2 Element, da diese die Festen Filter "Alle" und "Bestellung" sind
        const ignoredElements = list.slice(0, 2);
        const arrToSort = list.slice(2);

        // Sortiere nach label
        arrToSort.sort((a, b) => a.label.localeCompare(b.label));

        // Ignorierte Elemente und sortierte Liste zusammenfügen
        const sortedArr = ignoredElements.concat(arrToSort);

        return sortedArr;
    }

    /**
     * @description PDEV-1683: auswaehlbare Chargen fuer den Mitarbeiter des Auftrags laden
     *              (ohne Mitarbeiter: Mitarbeiter des eingeloggten Benutzers)
     */
    private loadEmployeeSampleBatches(): void {
        const employeeId = Number(this.orderDataCopy?.employee_id) || null;
        // fuer diesen Mitarbeiter bereits geladen bzw. in Arbeit
        if (
            employeeId === this.#employeeSampleBatchesEmployeeId &&
            (this.#employeeSampleBatches !== null || this.#employeeSampleBatchesLoading)
        ) {
            return;
        }

        this.#employeeSampleBatchesEmployeeId = employeeId;
        this.#employeeSampleBatches = null;
        this.#employeeSampleBatchesLoading = true;

        this.sampleBatchesService
            .getEmployeeSampleBatches(employeeId)
            .pipe(takeUntil(this.#componentDestroyed$))
            .subscribe((result: EmployeeSampleBatches) => {
                // Antwort zu einem inzwischen gewechselten Mitarbeiter verwerfen
                if (employeeId !== this.#employeeSampleBatchesEmployeeId) {
                    return;
                }
                this.#employeeSampleBatches = result;
                this.#employeeSampleBatchesLoading = false;
                this.getProductBatchesForSelectData();
            });
    }

    /**
     * Bereite die verfügbaren Produktchargen für Auswahlliste vor
     *
     * PDEV-1683: eingeschraenkt auf die auswaehlbaren Chargen des Auftrags-Mitarbeiters
     * (Musterlager), inkl. Bestand je Charge (available_stock) fuer die rote Markierung
     */
    private getProductBatchesForSelectData() {
        const employeeBatches = this.#employeeSampleBatches;
        const selectDataProductBatches: SelectData[] = [];

        for (const [key, value] of Object.entries(this.productBatchList)) {
            selectDataProductBatches[key] = [];
            for (const val of value) {
                // Solange die Chargen geladen werden, keine (evtl. falschen) Chargen anbieten
                if (this.#employeeSampleBatchesLoading) {
                    continue;
                }

                if (employeeBatches?.restricted) {
                    const batchKey = SampleBatchesService.key(val.product_id, val.charge);
                    // nicht auswaehlbare Charge zaehlt wie im Backend als Bestand 0
                    val.available_stock = employeeBatches.stock[batchKey] ?? 0;
                    if (!hasOwn(employeeBatches.stock, batchKey)) {
                        continue;
                    }
                } else {
                    delete val.available_stock;
                }

                const validFrom = moment(val.valid_from);
                const validTo = moment(val.valid_to);
                /*
                 * Da in der auftragsübersicht die chargen nur relevant sind wenn es perönlich direkt abgegeben wurde
                 * wird nur geprüft ob die charge jetzt gerade valide ist
                 */
                if (!val.locked && validFrom.isSameOrBefore(moment()) && moment().isSameOrBefore(validTo)) {
                    selectDataProductBatches[key].push({
                        id: val.charge,
                        label: val.charge,
                    });
                }
            }
        }

        // neu zuweisen, damit die Auswahlfelder die geaenderten Listen sicher uebernehmen
        this.selectDataProductBatches = selectDataProductBatches;
    }

    /*
     * Initialisierung des Forms
     */
    private initForm() {
        const result = {};
        result['productsFormArray'] = this.getProductFormControls();
        return new UntypedFormGroup(result);
    }

    /**
     * Initialisierung der Form-Controls für die Auftragspositionen
     * @returns {FormArray}
     */
    private getProductFormControls() {
        // Falls keine produkte exisiteren, keine anzeigen
        if (!this.checkIfProductsExits()) {
            return new UntypedFormArray([]);
        }

        const result = [];
        for (const [orderRangeKey, orderRange] of Object.entries(this.orderEntryProductList[this.selectedOrderType])) {
            for (const childProducts of Object.values(orderRange)) {
                for (const productEntity of Object.values(childProducts)) {
                    // zusätzliche Dimension für mehrere Lieferempfänger, es wird eine Zeile je Produkt+Empfänger erstellt
                    const numOfDeliveryInstitutions = this.orderDataCopy?.deliveryInstitutions?.length ?? 1;

                    for (const recipient of numberRange(1, numOfDeliveryInstitutions)) {
                        /*
                         * Der Teil hat derzeit keinen Einfluss, die delivery_institution_id wird beim speichern auf die aktuell ausgewählte Einrichtung gesetzt
                         * TODO: war anscheinend nur für Klinge - raus?
                         */
                        let orderItemDeliveryInstitutionId = 0;
                        if (hasOwn(this.orderDataCopy ?? null, 'deliveryInstitutions')) {
                            orderItemDeliveryInstitutionId =
                                this.orderDataCopy?.deliveryInstitutions[recipient - 1] ?? null;
                        }

                        const isSecDiscEnabled = this.isSecondaryDiscountEnabled(productEntity['id'], orderRangeKey);
                        const isFreeAmountEnabled = this.isFreeAmountEnabled(productEntity['id'], orderRangeKey);

                        const childResult = {
                            product_id: new UntypedFormControl(productEntity['id']),
                            product_erp_number: new UntypedFormControl(productEntity['erp_number']),
                            pzn: new UntypedFormControl(productEntity['pzn']),
                            order_item_delivery_institution_id: new UntypedFormControl(orderItemDeliveryInstitutionId),
                            order_item_delivery_institution_index: new UntypedFormControl(recipient),
                            parent_product_id: new UntypedFormControl(productEntity['parent_id']),
                            product_label: new UntypedFormControl(productEntity['label']),
                            product_type: new UntypedFormControl(productEntity['product_type']),
                            amount: new UntypedFormControl(0, Validators.min(0)),
                            amountPerDeliveryDate: this.createDeliveryDatesFormArray(productEntity['id'], orderRangeKey),
                            unit_price: new UntypedFormControl(productEntity['price']),
                            discount: new UntypedFormControl(this.orderDataCopy.order_discount ?? 0, [
                                Validators.min(0),
                                Validators.max(100),
                            ]),
                            flat_discount: new UntypedFormControl(null),
                            special_price: new UntypedFormControl(null),
                            charge: new UntypedFormControl(null),
                            total_price: new UntypedFormControl(null),
                            quantity: new UntypedFormControl(productEntity['quantity']),
                            max_amount: new UntypedFormControl(productEntity['max_amount']),
                            valid_from: new UntypedFormControl(productEntity['valid_from']),
                            valid_to: new UntypedFormControl(productEntity['valid_to']),
                            // speichern des list_key für Sortiment, damit man später noch weiß zu welchem Sortiment das Produkt gehört
                            order_range: new UntypedFormControl(orderRangeKey),
                            amount_free: new UntypedFormControl(0, [
                                Validators.min(0),
                                this.freeAmountMaxValidator(productEntity['id'], orderRangeKey),
                            ]),
                            secondary_discount: new UntypedFormControl({
                                value: null,
                                disabled: !isSecDiscEnabled,
                            }),
                            reason_secondary_discount: new UntypedFormControl({
                                value: null,
                                disabled: !isSecDiscEnabled,
                            }),
                            reason_amount_free: new UntypedFormControl({
                                value: null,
                                disabled: !isFreeAmountEnabled,
                            }),
                            valuta_date: new UntypedFormControl(null),
                            is_duplicate: new UntypedFormControl(false), // um Duplikate zu markieren
                            duplicate_position: new UntypedFormControl(0),
                        };
                        result.push(new UntypedFormGroup(childResult));

                        // Listener für die Eingabefelder dieses Produkts registrieren und in Liste der Subscriptions aufnehmen
                        this.productItemFieldsSubscriptions.add(
                            childResult.amountPerDeliveryDate.valueChanges.subscribe((arr) => {
                                const paid = arr.map((d) => d.amountAtDate).reduce(toSum, 0);
                                const free = arr.map((d) => d.amountFreeAtDate).reduce(toSum, 0);
                                // Menge
                                childResult.amount.setValue(paid);
                                // Menge kostenlos
                                childResult.amount_free.setValue(free);
                            }),
                        );

                        // Der aufsummierte Amount und Rabatt und Sekundärrabatt des Items gehen in die weitere Berechnung der Summen/Rabatte ein
                        this.productItemFieldsSubscriptions.add(
                            merge(
                                childResult.amount.valueChanges,
                                childResult.discount.valueChanges,
                                childResult.secondary_discount.valueChanges,
                            ).subscribe(() => {
                                const amount = (childResult.amount.value as number) ?? 0;
                                const unitPrice = (childResult.unit_price.value as number) ?? 0;

                                /*
                                 * Rabatt
                                 * Wird nur mit aktiven accessRights angewendet
                                 */
                                let discount = (childResult.discount.value as number) ?? 0;
                                if (!this.orderFormService.orderAccessRightsExplicit['order_item_discount']['ngIf']) {
                                    discount = 0;
                                }

                                /*
                                 * Sekundärrabatt
                                 * Wird nur mit aktiven accessRights angewendet
                                 */
                                let secondaryDiscount = (childResult.secondary_discount.value as number) ?? 0;
                                if (
                                    !this.orderFormService.orderAccessRightsExplicit['order_item_secondary_discount'][
                                        'ngIf'
                                    ]
                                ) {
                                    secondaryDiscount = 0;
                                }

                                // Preis mit Rabatt
                                const priceAfterPrimary = amount * unitPrice * (1 - discount);

                                // Preis mit Rabatt und ggf. Sekundärrabatt
                                const finalTotal = priceAfterPrimary * (1 - secondaryDiscount);

                                // Control updaten
                                childResult.total_price.setValue(finalTotal);
                            }),
                        );
                    }
                }
            }
        }

        // Nach label sortieren
        this.sortProductControlsByLabel(result);

        return new UntypedFormArray(result);
    }

    /**
     * @brief Variante für die Mengen-Buttons bei den Auftragspositionen mit mehreren Lieferdaten,
     *        falls eine Quantity gesetzt ist, wird der amount um diesen Wert erhöht
     * @param {number} productIndex
     * @param {number} dateIndex
     * @param {number} multiplier
     */
    changeAmountAtDate(productIndex: number, dateIndex: number, multiplier: number) {
        const orderItemForm = this.orderItemFormGroup.controls.productsFormArray['controls'][productIndex];
        const currentQuantity = parseInt(orderItemForm.value.quantity, 10);
        const deliveryDateAmountControl =
            orderItemForm.controls.amountPerDeliveryDate.controls[dateIndex].controls.amountAtDate;

        const currentAmmountValue = parseInt(deliveryDateAmountControl.value, 10);

        let amount = multiplier;
        if (currentQuantity > 0) {
            amount = currentQuantity * multiplier;
        }

        if (currentAmmountValue + amount < 0) {
            deliveryDateAmountControl.setValue(0);
        } else {
            deliveryDateAmountControl.setValue(currentAmmountValue + amount);
        }

        this.overwriteDiscounts(productIndex);
    }

    /**
     * @brief Variante für die kostenlose Menge Buttons bei den Auftragspositionen mit mehreren Lieferdaten,
     *        falls eine Quantity gesetzt ist, wird der amount_free um diesen Wert erhöht
     * @param {number} productIndex
     * @param {number} dateIndex
     * @param {number} multiplier
     */
    public changeAmountFreeAtDate(productIndex: number, dateIndex: number, multiplier: number): void {
        const orderItemForm = this.orderItemFormGroup.controls.productsFormArray['controls'][productIndex];
        const currentQuantity = parseInt(orderItemForm.value.quantity, 10);
        const freeControl = orderItemForm.controls.amountPerDeliveryDate.controls[dateIndex].controls.amountFreeAtDate;
        const freeAmountMax = this.getFreeAmountMax(orderItemForm.value.product_id, orderItemForm.value.order_range);

        const currentFreeValue = parseInt(freeControl.value, 10);

        let freeAmount = multiplier;
        if (currentQuantity > 0) {
            freeAmount = currentQuantity * multiplier;
        }

        if (currentFreeValue + freeAmount < 0) {
            freeControl.setValue(0);
        } else {
            // Limit auf 0..max. kostenlose Menge des Produkts setzen
            const newFreeValue = Math.max(0, Math.min(freeAmountMax, currentFreeValue + freeAmount));
            freeControl.setValue(newFreeValue);
        }
    }

    /**
     * Überschreibt die Rabatte für ein bestimmtes Produkt auf die beste verfügbare Rabattregel
     * @param productIndex Index des Produkts
     */
    overwriteDiscounts(productIndex: number): void {
        const productCtrl = this.orderItemFormGroup.controls.productsFormArray['controls'][
            productIndex
        ] as UntypedFormGroup;
        const productId = Number(productCtrl.controls.product_id.value);
        const groupIds = this.getGroupIdsForProduct(productId);

        if (groupIds.length === 0) {
            /*
             * Keine Gruppenrabatte
             * Beste verfügbare Rabattregel anwenden
             */
            this.applyBestDiscountForProduct(productCtrl);
            this.isInitialDiscountLoad = false;
        } else {
            // Gruppenrabatte anwenden
            this.recomputeForGroups(groupIds);
        }
    }

    /**
     * Sammelt alle Rabattgruppen und rechnet die Rabatte aller betroffenen Produkte neu
     */
    private updateAllDiscountGroups(): void {
        if (!Array.isArray(this.discounts) || this.discounts.length === 0) return;

        const productsFormArray = this.orderItemFormGroup.get('productsFormArray') as UntypedFormArray | null;
        if (!productsFormArray) return;

        const allProductControls = productsFormArray.controls as UntypedFormGroup[];
        const groupIds = new Set<number>();

        allProductControls.forEach((productControl) => {
            const productId = productControl.controls.product_id.value as number;
            const rules = this.getProductDiscountRules(productId);

            // Rabattregeln für Gruppen zwischenspeichern
            rules.forEach((r) => {
                if (r.discount_group_id && r.discount_group_id !== productId) {
                    groupIds.add(r.discount_group_id);
                }
            });
        });

        // Rabatte anwenden
        this.recomputeForGroups([...groupIds]);
    }

    /*
     * Auswahl des Sortiments
     */
    selectProductRange(event: number | string) {
        this.selectedOrderRange = event;

        // Auswahlfelder für Produktgruppen aktualisieren
        this.selectDataProductGroups = [];
        this.getProductGroupsForSelectData();
        // Filter zurücksetzen
        this.selectedParentProductId = 'all';
        this.selectedProductType = 'all';
    }

    /*
     * Auswahl der Produktgruppe
     */
    selectParentProductId(event: number | string) {
        this.selectedParentProductId = event;
    }

    /**
     * Prüfe ob Produkttyp filter mit dem gegebenen Produkttyp passt
     * @param itemType der product_type des Items
     * @returns true, wenn "alle" ausgewählt oder der Produkttyp exakt passt
     */
    private checkProductTypeFilter(itemType: string): boolean {
        return this.selectedProductType === 'all' || this.selectedProductType === itemType;
    }

    /**
     * @brief Prüfe ob Formular für Items sichbar sein soll
     * @details Bei einer Neuanlage eines Auftrags:
     *          Sichtbar wenn man grade im EditMode ist, eine Auftragsart ausgewählt ist und
     *          Produkte unter der ausgewählten Auftragsart vorhanden sind
     * @returns {boolean}
     */
    public checkIfOrderFormVisible(): boolean {
        // Prüfe ob es sich um eine Neuanlage handelt
        if (notEmpty(this.orderDataCopy) && notEmpty(this.orderDataCopy.id) && this.orderDataCopy.id > 0) {
            return this.editMode && !this.loading;
        }
        return (
            this.editMode &&
            !this.loading &&
            this.selectedOrderType !== undefined &&
            this.orderEntryProductList[this.selectedOrderType]
        );
    }

    /**
     * @brief Prüft ob einzelne OrderItems sichtbar sind
     * @param {FormControl} control
     * @returns {boolean}
     */
    public checkIfOrderItemVisible(control: UntypedFormControl): boolean {
        // Produkt Sortiment ermitteln
        const productOrderRange = control.get('order_range').value;
        // Parent-ID ermitteln
        const productParentId = control.get('parent_product_id').value;
        // Menge ermitteln
        const productAmount = control.get('amount').value;
        // Produkttyp
        const productType = control.get('product_type').value;
        // Control gültig?
        const isControlValid = control.valid;

        // Prüft, ob Sortiment und Produktgruppe passt
        const passesProductRangeAndGroupFilter = this.checkProductRangeAndGroupFilter(
            productOrderRange,
            productParentId,
            productAmount,
            isControlValid,
        );

        // Prüft, ob Produkttyp passt
        const passesProductTypeFilter = this.checkProductTypeFilter(productType);

        // Such-Filter aktiv?
        if (this.productSearchActive) {
            const needle = this.productSearchValue.toLowerCase();
            for (const field of this.productDataFieldsForSearch) {
                const val = control.get(field).value;
                const text = typeof val === 'number' ? val.toString() : (val || '').toLowerCase();
                if (text.includes(needle)) {
                    // Wenn Suche passt, zusätzlich beide anderen Filter prüfen
                    return passesProductRangeAndGroupFilter && passesProductTypeFilter;
                }
            }
            return false;
        }

        // Kein Such-Filter: nur Range+Group und Type
        return passesProductRangeAndGroupFilter && passesProductTypeFilter;
    }

    /**
     * @brief Prüft ob Sortimentsfilter aktiv ist
     * @param {string} orderRange
     * @returns {boolean}
     */
    private checkProductRangeFilter(orderRange: string): boolean {
        // Prüfe ob "Alle" Filter ausgewählt ist
        if (this.selectedOrderRange === 'all') {
            return true;
        }
        return this.selectedOrderRange == orderRange;
    }

    /**
     * @brief Prüft ob Produkte unter dem ausgewählten Produktfilter sichtbar sein sollen
     * @param {number} productParentId
     * @param isControlValid
     * @param {number} productAmount
     * @returns {boolean}
     */
    private checkProductGroupFilter(productParentId: number, productAmount: number, isControlValid: boolean): boolean {
        // Prüfe ob Produktgruppe oder spezieller Filter ausgewählt ist
        if (this.selectedParentProductId == 'all') {
            // Gebe alle Produkte zurück beim Filter "Alle"
            return true;
        }
        if (this.selectedParentProductId == 'amountSet') {
            // Gebe alle Produkte dessen Menge gesetzt ist zurück
            return productAmount > 0 || !isControlValid;
        }
        // Gebe Produkte der ausgewählten Produktgruppe zurück
        return this.checkTopLevelProductGroup(productParentId);
    }

    /*
     * @brief Prüft ob Produkt direktes Child oder Subchild der höchsten Produkt-Gruppen Ebene ist
     */
    private checkTopLevelProductGroup(productParentId: number): boolean {
        if (this.selectedParentProductId == productParentId) {
            return true;
        }
        if (this.productGroupList[productParentId].parent_id === null) {
            return false;
        }
        return this.checkTopLevelProductGroup(this.productGroupList[productParentId].parent_id);
    }

    /**
     * @brief Prüfung auf Sortiment und Produktgruppen Filter
     * @param {string} orderRange
     * @param {number} productParentId
     * @param isControlValid
     * @param {number} productAmount
     * @returns {boolean}
     */
    private checkProductRangeAndGroupFilter(
        orderRange: string,
        productParentId: number,
        productAmount: number,
        isControlValid: boolean,
    ) {
        return (
            this.checkProductRangeFilter(orderRange) &&
            this.checkProductGroupFilter(productParentId, productAmount, isControlValid)
        );
    }

    /**
     * Suche nach Produkten in der Produktliste
     * @param {string} searchValue Wert im Such-Input-Feld
     */
    public searchProducts(searchValue: string) {
        // Prüfe vorerst ob ein Suchwert eingegeben worden ist
        if (notEmpty(searchValue) && typeof searchValue !== 'object') {
            this.productSearchActive = true;
        }
    }

    /**
     * Suche nach Produkten aufheben
     */
    public resetProductSearch() {
        this.productSearchActive = false;
        this.productSearchValue = null;
    }

    /**
     * @brief Wenn Lieferdaten hinzugefügt oder entfernt wurden, das interne Modell aktualisieren
     * @param {Date[]} deliveryDates
     * @private
     */
    private onChangedDeliveryDates(deliveryDates: Date[]) {
        // zunächst Werte der Felder auf 0 setzen, die durch die Änderung unsichtbar/unbenutzt werden
        if (
            notEmptyObject(this.deliveryDates) &&
            notEmptyObject(deliveryDates) &&
            notEmptyObject(this.orderItemFormGroup.controls.productsFormArray)
        ) {
            if (deliveryDates.length < this.deliveryDates.length) {
                const productsFormArrayControls = this.orderItemFormGroup.controls.productsFormArray['controls'];
                for (const p of numberRange(0, productsFormArrayControls.length - 1)) {
                    const amountControls = productsFormArrayControls[p]?.controls?.amountPerDeliveryDate?.controls;
                    if (notEmptyObject(amountControls)) {
                        // je nach Zustand der Initialisierung existieren noch nicht alle Controls, daher Zugriffe absichern
                        for (const d of numberRange(
                            deliveryDates.length,
                            Math.min(amountControls.length, this.deliveryDates.length - 1),
                        )) {
                            amountControls[d]?.controls?.amountAtDate?.setValue(0);
                        }
                    }
                }
            }
        }
        this.visibleDeliveryDates = deliveryDates.length;
        this.rebuildDeliveryDateIndexes();

        this.deliveryDates = deliveryDates;
    }

    /**
     * @brief Erzeugt einen FormArray für die Mengen-Eingaben je Lieferdatum an einem Item
     * @param {number} productId id des Produkts (für die max. kostenlose Menge)
     * @param {string} orderRange list_key des Sortiments der Zeile
     * @returns {FormArray}
     * @private
     */
    private createDeliveryDatesFormArray(productId: number, orderRange: string) {
        // maximale Anzahl möglicher Lieferdaten erstellen, damit die Controls nur jeweils ein- und ausgeblendet werden müssen
        return new UntypedFormArray(
            numberRange(1, this.orderFormService.maxDeliveryDates).map(
                () =>
                    new UntypedFormGroup({
                        amountAtDate: new UntypedFormControl(0, Validators.min(0)),
                        amountFreeAtDate: new UntypedFormControl(0, [
                            Validators.min(0),
                            this.freeAmountMaxValidator(productId, orderRange),
                        ]),
                    }),
            ),
        );
    }

    /**
     * @brief   Prüft ob ein Produkt für ein bestimmtes Lieferdatum gültig ist
     * @param   {any}       productItem der Auftragsposition
     * @param   {number}    dateIndex   Index des Lieferdatums (1, 2, 3 oder 4)
     * @returns {boolean}               true wenn das Produkt für das Lieferdatum gültig ist
     */
    public isProductValidForDeliveryDate(productItem: any, dateIndex: number): boolean {
        const deliveryDate = this.deliveryDates?.[dateIndex];
        if (!deliveryDate) {
            return true;
        }
        const validFrom = productItem.get('valid_from').value;
        const validTo = productItem.get('valid_to').value;
        if (validFrom && moment(deliveryDate).isBefore(moment(validFrom), 'day')) {
            return false;
        }
        if (validTo && moment(deliveryDate).isAfter(moment(validTo), 'day')) {
            return false;
        }
        return true;
    }

    /**
     * @brief Prüft ob die eingegebene Bestellmenge eines Produkts über die Mindestbestellmenge und unter der Maximalmenge liegt
     * @param {FormControl} control Feld mit Daten
     * @param {number} index Lieferdatum 1, 2, 3 oder 4
     * @returns {boolean}
     */
    public checkMaxAndMinOrderAmount(control: UntypedFormControl, index: number): boolean {
        // IDs ermitteln
        const productId = control.get('product_id').value;
        const parentId = control.get('parent_product_id').value;

        // Produkt Sortiment ermitteln
        const orderRangeKey = control.get('order_range').value;

        // Produkte holen
        const products = this.orderEntryProductList[this.selectedOrderType][orderRangeKey][parentId];

        // Maximal und Minimal Menge ermitteln
        const maxAmount = products?.find((item) => item.id === productId).max_amount;
        const minAmount = products?.find((item) => item.id === productId).min_amount;

        if (maxAmount === null && minAmount === null) {
            return false;
        }

        // momentan eigegebene Menge ermitteln
        const productAmountArray = control.get('amountPerDeliveryDate').value;
        let productAmount = 0;

        if (productAmountArray.length > index) {
            productAmount = productAmountArray[index].amountAtDate;
        }

        // Ausgabe ob Menge über- oder unterschritten worden ist, ignoriere falls keine Bestellung vom Produkt (Menge = 0)
        return (productAmount > maxAmount || productAmount < minAmount) && productAmount !== 0;
    }

    /**
     * @description PDEV-1483: Musterlager-Bestand der aktuell ausgewaehlten Charge - fuer die
     *              rote Markierung/Blockierung der Menge, analog
     *              ContactsItemsComponent.getMaxValue() im Kontaktbericht. Nur Pohl Boskamp
     *              (Backend-Gate) - available_stock fehlt komplett, wenn das Gate aus ist
     *              (andere Kunden) oder keine Charge ausgewaehlt ist, dann bleibt das Feld
     *              unbegrenzt (bestehendes Verhalten, siehe checkMaxAndMinOrderAmount fuer die
     *              davon unabhaengige, rein optische Mindest-/Hoechstmengen-Pruefung).
     * @param {FormControl} control Feld mit Daten
     * @returns {number} verfuegbarer Bestand der ausgewaehlten Charge, oder undefined
     */
    public getChargeAvailableStock(control: UntypedFormControl): number {
        const productId = control.get('product_id').value;
        const charge = control.get('charge').value;
        const batch = (this.productBatchList[productId] || []).find((b) => b.charge === charge);
        return batch?.available_stock ?? undefined;
    }

    /**
     * @description PDEV-1483: gut lesbare deutsche Fehlermeldung statt der generischen
     *              ">=0 und <=X sein"-Formulierung der geteilten Komponente
     * @param {FormControl} control Feld mit Daten
     * @returns {string} Fehlermeldung
     */
    public getChargeAvailableStockErrorMessage(control: UntypedFormControl): string {
        return `Menge muss zwischen 0 und ${this.getChargeAvailableStock(control)} liegen.`;
    }

    /**
     * @description PDEV-1683: Grenze fuer den Mengen-Validator - der Musterlager-Bestand sperrt
     *              nur, wenn blockSaveOnNegativeStock nicht auf "nur warnen" steht
     * @param {FormControl} control Feld mit Daten
     * @returns {number} Bestand als Obergrenze oder undefined (keine Grenze)
     */
    public getChargeAvailableStockMaxValue(control: UntypedFormControl): number {
        return this.blockSaveOnNegativeStock ? this.getChargeAvailableStock(control) : undefined;
    }

    /**
     * @description PDEV-1683: Gesamtmenge der Position uebersteigt den Musterlager-Bestand der
     *              gewaehlten Charge (rote Markierung im Warn-Modus)
     * @param {FormControl} control Feld mit Daten
     * @returns {boolean} true bei Ueberschreitung
     */
    public isChargeStockExceeded(control: UntypedFormControl): boolean {
        const availableStock = this.getChargeAvailableStock(control);
        return availableStock !== undefined && (Number(control.get('amount').value) || 0) > availableStock;
    }

    /**
     * @description PDEV-1683: Positionen, deren Menge den Musterlager-Bestand uebersteigt - fuer die
     *              Rueckfrage vor dem Speichern (Bestaetigen/Korrigieren). Nur im Warn-Modus.
     * @returns {string[]} ein Hinweistext je betroffener Position, leer wenn alles passt
     */
    public getStockWarnings(): string[] {
        if (this.blockSaveOnNegativeStock) {
            return [];
        }

        const orderItemFormGroups = this.orderItemFormGroup?.controls?.productsFormArray?.['controls'] ?? [];
        return orderItemFormGroups
            .filter((control: UntypedFormControl) => this.isChargeStockExceeded(control))
            .map(
                (control: UntypedFormControl) =>
                    `${control.get('product_label').value}, Charge ${control.get('charge').value}: ` +
                    `Menge ${control.get('amount').value}, Musterlager-Bestand ${this.getChargeAvailableStock(control)}`,
            );
    }

    /**
     * @description PDEV-1483: Validierungsfehler des amountAtDate-Feldes fuer ein bestimmtes
     *              Lieferdatum - Hilfsmethode, damit das Template nicht die volle
     *              FormArray/FormGroup-Verschachtelung ausschreiben muss.
     * @param {FormControl} control Feld mit Daten
     * @param {number} index Lieferdatum 1, 2, 3 oder 4
     * @returns {object} Validierungsfehler des Feldes, oder null
     */
    public getAmountAtDateErrors(control: UntypedFormControl, index: number) {
        return control.get('amountPerDeliveryDate').get([index, 'amountAtDate']).errors;
    }

    /**
     * @brief Wendet angegebenen Rabatt auf alle Auftragspositionen an
     * @param {number} discountNew
     */
    private applyDiscount(discountNew: number) {
        // PDEV-1775: uebernommener Kopf-Rabatt gilt ab jetzt als Standardrabatt der Positionen
        this.standardDiscountOverride = discountNew;
        const orderItemFormGroups = this.orderItemFormGroup.controls.productsFormArray['controls'];
        for (const orderItem of orderItemFormGroups) {
            orderItem.patchValue({discount: discountNew});
        }
    }

    /**
     * prüft ob produkte für die gewählte Werte existieren
     * @returns
     */
    private checkIfProductsExits() {
        return !(
            this.orderEntryProductList == undefined ||
            this.orderEntryProductList == undefined ||
            this.orderEntryProductList[this.selectedOrderType] == undefined
        );
    }

    /**
     * Prüft ob der sekundäre Rabatt für ein Produkt freigeschaltet ist.
     * Maßgeblich ist das Flag secondary_discount_enabled am Preis-Datensatz (product_order_information)
     * der gewählten Auftragsart und des Sortiments.
     *
     * @param productId id des Produkts
     * @param orderRange list_key des Sortiments der Zeile
     * @returns true, wenn sekundärer Rabatt für dieses Produkt freigeschaltet ist
     */
    public isSecondaryDiscountEnabled(productId: number, orderRange: string): boolean {
        return this.getOrderFlags(productId, orderRange)?.secondaryDiscount ?? false;
    }

    /**
     * Prüft ob kostenlose Mengen für ein Produkt freigeschaltet sind.
     * Maßgeblich ist das Flag free_amount_enabled am Preis-Datensatz (product_order_information)
     * der gewählten Auftragsart und des Sortiments.
     *
     * @param productId id des Produkts
     * @param orderRange list_key des Sortiments der Zeile
     * @returns true, wenn kostenlose Menge für dieses Produkt freigeschaltet ist
     */
    public isFreeAmountEnabled(productId: number, orderRange: string): boolean {
        return this.getOrderFlags(productId, orderRange)?.freeAmount ?? false;
    }

    /**
     * Liefert die maximale kostenlose Menge eines Produkts.
     * Maßgeblich ist free_amount_max am Preis-Datensatz (product_order_information) der gewählten
     * Auftragsart und des Sortiments, Fallback ist der Config-Wert productsFreeSampleMaxAmount.
     *
     * @param productId id des Produkts
     * @param orderRange list_key des Sortiments der Zeile
     * @returns maximale kostenlose Menge
     */
    public getFreeAmountMax(productId: number, orderRange: string): number {
        return this.getOrderFlags(Number(productId), orderRange)?.freeAmountMax ?? this.productsFreeSampleMaxAmount;
    }

    /**
     * Validator für die max. kostenlose Menge eines Produkts.
     * Der Wert wird erst bei der Validierung ermittelt, damit nachgeladene Config/Produktliste berücksichtigt werden.
     *
     * @param productId id des Produkts
     * @param orderRange list_key des Sortiments der Zeile
     */
    private freeAmountMaxValidator(productId: number, orderRange: string): ValidatorFn {
        return (control: AbstractControl) => Validators.max(this.getFreeAmountMax(productId, orderRange))(control);
    }

    /**
     * Liefert die Freischaltungen des Preis-Datensatzes eines Produkts in der gewählten Auftragsart.
     * Die Map wird nur neu aufgebaut, wenn sich Produktliste oder Auftragsart geändert haben,
     * da die Prüfung auch aus dem Template heraus aufgerufen wird.
     *
     * @param productId id des Produkts
     * @param orderRange list_key des Sortiments der Zeile
     */
    private getOrderFlags(
        productId: number,
        orderRange: string,
    ): {secondaryDiscount: boolean; freeAmount: boolean; freeAmountMax: number | null} | undefined {
        if (
            this.orderFlagsSource.productList !== this.orderEntryProductList ||
            this.orderFlagsSource.orderType !== this.selectedOrderType
        ) {
            this.orderFlags.clear();
            this.orderFlagsSource = {productList: this.orderEntryProductList, orderType: this.selectedOrderType};

            for (const [orderRangeKey, productsByParent] of Object.entries(
                this.orderEntryProductList?.[this.selectedOrderType] ?? {},
            )) {
                for (const products of Object.values(productsByParent as LooseObject)) {
                    for (const product of Object.values(products as LooseObject[])) {
                        this.orderFlags.set(`${orderRangeKey}|${product['id']}`, {
                            secondaryDiscount: product['secondary_discount_enabled'] === true,
                            freeAmount: product['free_amount_enabled'] === true,
                            freeAmountMax: product['free_amount_max'] > 0 ? Number(product['free_amount_max']) : null,
                        });
                    }
                }
            }
        }

        return this.orderFlags.get(`${orderRange}|${productId}`);
    }

    /**
     * Listentries laden
     */
    loadListentries() {
        // selectDataProductTypes
        this.listentriesService.loadListForSelect('productType', [{id: 'all', label: 'Alle'}]).then((value) => {
            this.selectDataProductTypes = value;

            this.selectedProductType =
                this.selectDataProductTypes.find((entry) => {
                    try {
                        const data = typeof entry.data === 'string' ? JSON.parse(entry.data) : entry.data;
                        return data?.preSelected;
                    } catch {
                        return false;
                    }
                })?.id ?? this.selectDataProductTypes[0]?.id;
        });

        // secondaryDiscountFactor
        this.listentriesService.loadListForSelect('orderItemSecondaryDiscountFactor').then((value) => {
            this.selectDataSecondaryDiscountFactor = value;
        });

        // orderItemAmountFreeReason
        this.listentriesService.loadListForSelect('orderItemAmountFreeReason').then((value) => {
            this.selectDataAmountFreeReason = value;
        });

        // orderItemSecondaryDiscountReason
        this.listentriesService.loadListForSelect('orderItemSecondaryDiscountReason').then((value) => {
            this.selectDataSecondaryDiscountReason = value;
        });
    }

    // Für jedes Lieferdatum einen Index erzeugen
    private rebuildDeliveryDateIndexes(): void {
        this.deliveryDateIndexes.length = 0;
        for (let i = 0; i < this.visibleDeliveryDates; i++) {
            this.deliveryDateIndexes.push(i);
        }
    }

    /**
     * Produkte in der FormGroup nach label sortieren.
     *
     * Hinweis: im Backend den orderBy zu setzen hat nichts gebracht.
     * Die Produkte werden weiter aufgesplittet und separat verarbeitet -> erschließt sich mir nicht
     * Deshalb am Ende sortieren wenn die Produkte schon in der FormGroup sind
     *
     * @param controls Produkte
     */
    private sortProductControlsByLabel(controls: UntypedFormGroup[]): void {
        controls.sort((a, b) => {
            const aLabel = String(a.get('product_label')?.value ?? '');
            const bLabel = String(b.get('product_label')?.value ?? '');
            return aLabel.localeCompare(bLabel, 'de', {sensitivity: 'base'});
        });
    }

    /**
     *
     * @param {number|string} ruleOrderType - Die Auftragsart der Rabattregel
     * @returns {boolean} - true, wenn die Auftragsart der Rabattregel die ausgewählte Auftragsart entspricht oder 0
     *
     * 0 = alle Auftragsarten
     */
    private orderTypeMatches(ruleOrderType: string): boolean {
        return ruleOrderType === this.selectedOrderType || ruleOrderType === '0';
    }

    /**
     * Prüft, ob die Rabattregel mindestens ein Wert hat
     *
     * @param rule Die zu prüfende Rabattregel
     * @returns true, wenn die Rabattregel mindestens ein Wert hat
     */
    private ruleHasAnyValue(rule: Discount): boolean {
        return (
            (Number(rule.discount) || 0) !== 0 ||
            (Number(rule.flat_discount) || 0) !== 0 ||
            (Number(rule.special_price) || 0) !== 0
        );
    }

    /**
     * Summiert die eigene Menge eines Produkts über alle Lieferdaten hinweg
     *
     * @param ctrl FormGroup des Produkts (enthält amountPerDeliveryDate)
     * @returns {number} Summe der amountAtDate-Werte über alle Lieferdaten hinweg
     */
    private getOwnAmountSum(ctrl: UntypedFormGroup): number {
        const arr = ctrl.controls.amountPerDeliveryDate?.value as {amountAtDate: number}[] | undefined;
        if (!Array.isArray(arr)) return 0;
        return arr.map((x) => Number(x?.amountAtDate || 0)).reduce((a, b) => a + b, 0);
    }

    /**
     * Ermittelt alle Rabattgruppen, zu denen ein Produkt gehört
     *
     * @param productId ID des Produkts
     * @returns {number[]} IDs der Rabattgruppen
     */
    private getGroupIdsForProduct(productId: number): number[] {
        const rules = this.getProductDiscountRules(productId);
        const ids = new Set<number>();

        for (const r of rules) {
            if (r.discount_group_id && r.discount_group_id !== productId) ids.add(Number(r.discount_group_id));
        }

        return [...ids];
    }

    /**
     * Ermittelt die beste normale Rabattregel (außerhalb einer Rabattgruppe) für ein Produkt anhand seiner eigener Menge
     * @param productId id des Produkts
     * @param ownAmount eigene Menge des Produkts
     * @returns {Discount} beste normale Rabattregel
     */
    private bestNormalDiscount(productId: number, ownAmount: number): Discount | null {
        const rules = this.getProductDiscountRules(productId);

        // Normale Rabattregeln für die ausgewählte Auftragsart ermitteln
        const normal = rules.filter(
            (r) => !r.discount_group_id && this.orderTypeMatches(r.order_type) && this.ruleHasAnyValue(r),
        );

        // Besten anwendbaren discount ermitteln
        return findBestRuleForQuantity(normal, ownAmount);
    }

    /**
     * Liefert die beste Gruppen-Rabattregel für ein Produkt basierend auf der aggregierten Gruppenmenge
     * Nur Regeln mit der angegebenen discount_group_id werden berücksichtigt
     *
     * @param productId id des Produkts
     * @param groupId id der Rabattgruppe
     * @param aggregatedAmount aggregierte Menge aller Produkte in der Gurppe
     * @returns {Discount} beste Gruppen-Rabattregel
     */
    private bestGroupDiscount(productId: number, groupId: number, aggregatedAmount: number): Discount | null {
        const rules = this.getProductDiscountRules(productId);

        // Nur Regeln mit der angegebenen discount_group_id werden berücksichtigt und passender Auftragsart
        const groupOnly = rules.filter(
            (r) => r.discount_group_id === groupId && this.orderTypeMatches(r.order_type) && this.ruleHasAnyValue(r),
        );

        // Besten anwendbaren discount ermitteln
        return findBestRuleForQuantity(groupOnly, aggregatedAmount);
    }

    /**
     * Vergleicht mehrere Rabattregeln und gibt die beste zurück
     *
     * @param {...(Discount|null)} candidates alle Rabattregeln des Produkts
     * @returns {Discount|null} Die beste gefundene Rabattregel oder null, wenn keine gültig ist
     */
    private pickBestRule(...candidates: Array<Discount | null>): Discount | null {
        const valid = candidates.filter((c): c is Discount => !!c);
        if (valid.length === 0) return null;
        return valid.reduce((best, next) => (compareDiscountRules(best, next) > 0 ? next : best));
    }

    /**
     * Gibt alle Rabatt-Maps zurück (eine Map je additional_key, z.B. "default", "RuT")
     */
    private getAllDiscountMaps(): Array<Record<string, Discount[]>> {
        const root = this.discounts?.[0] as unknown;

        if (!root || typeof root !== 'object' || Array.isArray(root)) {
            return [];
        }

        return Object.values(root).filter(this.isDiscountMap);
    }

    /**
     * Type-Guard: prüft, ob ein Wert eine Rabatt-Map ist.
     */
    private isDiscountMap(value: unknown): value is Record<string, Discount[]> {
        return !!value && typeof value === 'object' && !Array.isArray(value);
    }

    /**
     * Holt alle Rabattregeln eines Produkts aus allen zusätzlichen Keys
     * (z.B. default + Kooperationen) und führt sie in einer Liste zusammen.
     */
    private getProductDiscountRules(productId: number | string): Discount[] {
        const pid = String(productId);
        const merged: Discount[] = [];

        for (const map of this.getAllDiscountMaps()) {
            const rules = map[pid];
            if (Array.isArray(rules)) {
                merged.push(...rules);
            }
        }

        return merged;
    }

    /**
     * Baut eine Map mit Gruppenaggregaten auf
     * Schlüssel ist die groupId, value enthält die betroffenen Produkte und die aggregierte Bestellmenge
     *
     * @param {number[]} groupIds Rabattgruppen-IDs
     * @returns {GroupAggregatesMapCtrl} Map groupId -> {productCtrls, qty}
     */
    private buildGroupAggregates(groupIds: number[]): GroupAggregatesMapCtrl {
        const productControls = this.orderItemFormGroup.controls.productsFormArray['controls'] as UntypedFormGroup[];
        const map: GroupAggregatesMapCtrl = new Map();

        for (const groupId of groupIds) {
            const members: UntypedFormGroup[] = [];
            let qty = 0;

            for (const product of productControls) {
                const productId = product.controls.product_id.value as number;
                const rules = this.getProductDiscountRules(productId);
                const inGroup = rules.some((r) => r.discount_group_id === groupId);

                // Produkt ist in der Gruppe
                if (inGroup) {
                    members.push(product);
                    qty += Number(product.controls.amount.value || 0);
                }
            }

            map.set(groupId, {productCtrls: members, qty});
        }

        return map;
    }

    /**
     * Ermittelt und setzt die beste Rabattregel für ein Produkt
     * Berücksichtigt:
     *  - die beste normale Regel (basierend auf eigener Menge)
     *  - die beste Gruppenregel je zugehöriger Rabattgruppe (basierend auf aggregierter Gruppenmenge)
     */
    private applyBestDiscountForProduct(productCtrl: UntypedFormGroup, preAgg?: GroupAggregatesMapCtrl): void {
        // Produkt-ID und eigene Menge des Produkts über alle Liefertermine
        const productId = productCtrl.controls.product_id.value as number;
        const ownAmount = this.getOwnAmountSum(productCtrl);

        // Beste normale Regel für dieses Produkt (ohne Gruppe)
        const bestNormal = this.bestNormalDiscount(productId, ownAmount);

        // Zugehörige Rabattgruppen bestimmen
        const groupIds = this.getGroupIdsForProduct(productId);
        // Berechnete Gruppenwerte (gesamte Menge in der Gruppe und zugehörige Produkte)
        const agg = preAgg ?? this.buildGroupAggregates(groupIds);

        // Für jede Gruppe die beste Gruppenregel anhand der Gruppen-Gesamtmenge ermitteln
        const groupCandidates: Array<Discount | null> = groupIds.map((groupId) => {
            const qty = agg.get(groupId)?.qty ?? this.computeGroupQuantity(groupId);
            return this.bestGroupDiscount(productId, groupId, qty);
        });

        // Beste Rabattregel ermitteln
        const best = this.pickBestRule(bestNormal, ...groupCandidates);

        if (best) {
            // Rabatt am Produkt setzen
            this.applyChosenDiscountToProduct(productCtrl, best);
            return;
        }

        /*
         * Keine Rabattregel gefunden:
         *  – Auf die zuvor gespeicherten Originalwerte zurücksetzen
         *  – Oder auf den Standardrabatt zurückfallen
         * PDEV-1775: Originalwerte gibt es nur fuer bereits gespeicherte Positionen. Neue Positionen fallen auch
         * bei der ersten Mengenaenderung auf den Standardrabatt zurueck (vorher: Rabatt geleert).
         */
        const original = this.originalDiscountValues.get(productId);
        if (this.isInitialDiscountLoad && original) {
            productCtrl.patchValue({
                discount: original.discount,
                flat_discount: original.flat_discount,
                special_price: original.special_price,
                valuta_date: original.valuta_date,
            });
        } else {
            /*
             * PDEV-1775: Greift (fuer die neue Menge) keine Rabattregel, gilt wieder der Standardrabatt
             * der Position (wie bei der Neuanlage vorbelegt) - nicht 0.
             */
            productCtrl.patchValue({
                discount: this.getStandardDiscount(),
                flat_discount: null,
                special_price: null,
                valuta_date: null,
            });
        }
    }

    /**
     * PDEV-1775: Standardrabatt einer Position - der im Kopf uebernommene Rabatt bzw. der Rabatt des
     * Auftrags (order_discount, i. d. R. Einrichtungsrabatt), mit dem neue Positionen vorbelegt werden.
     */
    private getStandardDiscount(): number {
        return this.standardDiscountOverride ?? this.orderDataCopy?.order_discount ?? 0;
    }

    // Summiert alle Menge der Produkte in einer Gruppe
    private computeGroupQuantity(gid: number): number {
        const ctrls =
            ((this.orderItemFormGroup.get('productsFormArray') as UntypedFormArray)?.controls as UntypedFormGroup[]) ||
            [];
        let total = 0;

        for (const ctrl of ctrls) {
            const pid = Number(ctrl.controls.product_id.value);
            const rules = this.getProductDiscountRules(pid);
            const inGroup = rules.some((r) => r.discount_group_id === gid);
            if (inGroup) total += Number(ctrl.controls.amount.value || 0);
        }
        return total;
    }

    /**
     * Rechnet alle betroffenen Produkte aus Rabattgruppen neu
     */
    private recomputeForGroups(groupIds: number[]): void {
        if (groupIds.length === 0) return;

        // Berechnete Gruppenwerte (gesamte Menge in der Gruppe und zugehörige Produkte)
        const aggregates = this.buildGroupAggregates(groupIds);
        const affected = new Set<UntypedFormGroup>();

        // Produkte aus der Gruppe zwischenspeichern
        for (const groupId of groupIds) {
            const entry = aggregates.get(groupId);
            entry?.productCtrls.forEach((p) => affected.add(p));
        }

        // Für jedes Produkt in der Gruppe die beste verfügbare Rabattregel anwenden
        for (const productCtrl of affected) {
            this.applyBestDiscountForProduct(productCtrl, aggregates);
        }

        this.isInitialDiscountLoad = false;
    }

    /**
     * Wendet eine gewählte Rabattregel auf ein Produkt an
     * Priorität: Prozent > Flat > Special
     *
     * @param {UntypedFormGroup} productCtrl FormgGroup des Produkts
     * @param {Discount|null} rule Zu setzende Regel oder null zum Leeren
     */
    private applyChosenDiscountToProduct(productCtrl: UntypedFormGroup, rule: Discount | null): void {
        if (!rule) {
            productCtrl.patchValue({discount: null, flat_discount: null, special_price: null, valuta_date: null});
            return;
        }

        const hasPercent = isValidNumber(Number(rule.discount)) && Number(rule.discount) !== 0;
        const hasFlat = isValidNumber(Number(rule.flat_discount)) && Number(rule.flat_discount) !== 0;
        const hasSpecial = isValidNumber(Number(rule.special_price)) && Number(rule.special_price) !== 0;

        if (hasPercent) {
            productCtrl.patchValue({
                discount: Number(rule.discount),
                flat_discount: null,
                special_price: null,
                valuta_date: rule.valuta_date ?? null,
            });
        } else if (hasFlat) {
            productCtrl.patchValue({
                discount: null,
                flat_discount: Number(rule.flat_discount),
                special_price: null,
                valuta_date: rule.valuta_date ?? null,
            });
        } else if (hasSpecial) {
            productCtrl.patchValue({
                discount: null,
                flat_discount: null,
                special_price: Number(rule.special_price),
                valuta_date: rule.valuta_date ?? null,
            });
        } else {
            productCtrl.patchValue({discount: null, flat_discount: null, special_price: null, valuta_date: null});
        }
    }

    // Für duplizierte Produkte Subscriptions anlegen, damit Preise und Mengen korrekt berechnet werden
    private attachProductSubscriptions(ctrl: UntypedFormGroup): void {
        const sub = new Subscription();
        const fdaArray = ctrl.controls.amountPerDeliveryDate as UntypedFormArray;
        const productId = Number(ctrl.controls.product_id.value);
        const orderRange = ctrl.controls.order_range.value;

        // Wenn sich die Werte der Lieferdaten ändern, summiere bezahlte und kostenlose Mengen neu
        sub.add(
            ctrl.controls.amountPerDeliveryDate.valueChanges.subscribe(
                (arr: Array<{amountAtDate: number; amountFreeAtDate: number}>) => {
                    if (!Array.isArray(arr)) return;

                    // Limitierung der kostenlosen Menge direkt auf 0..max. kostenlose Menge des Produkts
                    const freeAmountMax = this.getFreeAmountMax(productId, orderRange);
                    arr.forEach((d, i) => {
                        const freeCtrl = (fdaArray.at(i) as UntypedFormGroup).controls.amountFreeAtDate;

                        if (Number(freeCtrl.value) > freeAmountMax) {
                            freeCtrl.setValue(freeAmountMax, {emitEvent: false});
                            d.amountFreeAtDate = freeAmountMax;
                        }
                        if (Number(freeCtrl.value) < 0) {
                            freeCtrl.setValue(0, {emitEvent: false});
                            d.amountFreeAtDate = 0;
                        }
                    });

                    // Summen berechnen
                    const paid = arr.map((d) => Number(d?.amountAtDate || 0)).reduce((a, b) => a + b, 0);
                    const free = arr.map((d) => Number(d?.amountFreeAtDate || 0)).reduce((a, b) => a + b, 0);

                    ctrl.controls.amount.setValue(paid, {emitEvent: false});
                    ctrl.controls.amount_free.setValue(free, {emitEvent: false});
                },
            ),
        );

        // Wenn Menge oder Rabatte geändert werden, Gesamtpreis neu berechnen
        sub.add(
            merge(
                ctrl.controls.amount.valueChanges,
                ctrl.controls.discount.valueChanges,
                ctrl.controls.secondary_discount.valueChanges,
            ).subscribe(() => {
                const amount = Number(ctrl.controls.amount.value || 0);
                const unitPrice = Number(ctrl.controls.unit_price.value || 0);

                let discount = Number(ctrl.controls.discount.value || 0);
                if (!this.orderFormService.orderAccessRightsExplicit['order_item_discount']['ngIf']) discount = 0;

                let secondary = Number(ctrl.controls.secondary_discount.value || 0);
                if (!this.orderFormService.orderAccessRightsExplicit['order_item_secondary_discount']['ngIf'])
                    secondary = 0;

                const priceAfterPrimary = amount * unitPrice * (1 - discount);
                const finalTotal = priceAfterPrimary * (1 - secondary);
                ctrl.controls.total_price.setValue(finalTotal, {emitEvent: false});
            }),
        );

        // Subscription speichern, damit sie beim Entfernen des Duplikats wieder gelöscht werden kann
        this.productRowSubscriptions.set(ctrl, sub);
        this.productItemFieldsSubscriptions.add(sub);
    }

    /**
     * Erzeugt eine neue Produkt-FormGroup
     * Wird genutzt, um ein Duplikat einer bestehenden Auftragsposition zu erstellen
     * @param source Ursprüngliche Produktzeile, deren Werte kopiert werden sollen
     * @param dupPos Position des Duplikats (0 = Original, 1 = erstes Duplikat, ...)
     */
    private buildDuplicateFromSource(source: UntypedFormGroup, dupPos: number): UntypedFormGroup {
        const productId = Number(source.get('product_id')?.value);
        const parentId = Number(source.get('parent_product_id')?.value);
        const orderRange = source.get('order_range')?.value;
        const freeAmountMax = this.getFreeAmountMax(productId, orderRange);

        /*
         * Prüfen, ob Sekundärrabatt-Felder im Original deaktiviert sind
         */
        const secDiscDisabled = source.get('secondary_discount')?.disabled === true;
        const secDiscReasonDisabled = source.get('reason_secondary_discount')?.disabled === true;

        /*
         * Menge, K-Menge, S-Rabatt und Gründe nur übernehmen, wenn per Config aktiviert
         * Ansonsten startet das Duplikat mit leeren Feldern
         */
        const copyValues = this.duplicationOrderItemsCopyValues;

        /*
         * Neue FormGroup mit allen relevanten Feldern aufbauen
         * TODO: verbessern, zu viel doppelter code mit den controls
         */
        const duplicateGroup = new UntypedFormGroup({
            product_id: new UntypedFormControl(source.get('product_id')?.value),
            product_erp_number: new UntypedFormControl(source.get('product_erp_number')?.value),
            pzn: new UntypedFormControl(source.get('pzn')?.value),
            order_item_delivery_institution_id: new UntypedFormControl(
                source.get('order_item_delivery_institution_id')?.value,
            ),
            order_item_delivery_institution_index: new UntypedFormControl(
                source.get('order_item_delivery_institution_index')?.value,
            ),
            parent_product_id: new UntypedFormControl(parentId),
            product_label: new UntypedFormControl(source.get('product_label')?.value),
            product_type: new UntypedFormControl(source.get('product_type')?.value),
            amount: new UntypedFormControl(copyValues ? source.get('amount')?.value : 0, Validators.min(0)),
            amountPerDeliveryDate: this.createDeliveryDatesFormArray(productId, orderRange),
            unit_price: new UntypedFormControl(source.get('unit_price')?.value),
            discount: new UntypedFormControl(source.get('discount')?.value, [Validators.min(0), Validators.max(100)]),
            flat_discount: new UntypedFormControl(source.get('flat_discount')?.value),
            special_price: new UntypedFormControl(source.get('special_price')?.value),
            charge: new UntypedFormControl(source.get('charge')?.value),
            total_price: new UntypedFormControl(copyValues ? source.get('total_price')?.value : null),
            quantity: new UntypedFormControl(source.get('quantity')?.value),
            max_amount: new UntypedFormControl(source.get('max_amount')?.value),
            valid_from: new UntypedFormControl(source.get('valid_from')?.value),
            valid_to: new UntypedFormControl(source.get('valid_to')?.value),
            order_range: new UntypedFormControl(source.get('order_range')?.value),
            amount_free: new UntypedFormControl(copyValues ? source.get('amount_free')?.value : 0, [
                Validators.min(0),
                this.freeAmountMaxValidator(productId, orderRange),
            ]),
            secondary_discount: new UntypedFormControl({
                value: copyValues ? source.get('secondary_discount')?.value : null,
                disabled: secDiscDisabled,
            }),
            reason_secondary_discount: new UntypedFormControl({
                value: copyValues ? source.get('reason_secondary_discount')?.value : null,
                disabled: secDiscReasonDisabled,
            }),
            reason_amount_free: new UntypedFormControl(copyValues ? source.get('reason_amount_free')?.value : null),
            valuta_date: new UntypedFormControl(source.get('valuta_date')?.value),
            is_duplicate: new UntypedFormControl(true), // Markiert dieses Zeile als Duplikat
            duplicate_position: new UntypedFormControl(Number(dupPos ?? 0)), // Reihenfolge innerhalb der Duplikate
        });

        const targetDeliveryArray = duplicateGroup.get('amountPerDeliveryDate') as UntypedFormArray;

        // Werte für Lieferdaten (amountPerDeliveryDate) vom Original übernehmen, wenn per Config aktiviert
        const sourceDeliveryArray = source.get('amountPerDeliveryDate')?.value as Array<{
            amountAtDate: number;
            amountFreeAtDate: number;
        }>;

        if (copyValues && Array.isArray(sourceDeliveryArray)) {
            // Nur so viele Einträge kopieren, wie in beiden Arrays existieren
            const copyCount = Math.min(sourceDeliveryArray.length, targetDeliveryArray.length);

            // Alle Lieferdatum-Einträge durchgehen und Werte kopieren
            for (let dateIndex = 0; dateIndex < copyCount; dateIndex += 1) {
                const targetDateGroup = targetDeliveryArray.at(dateIndex) as UntypedFormGroup;

                targetDateGroup.patchValue(
                    {
                        amountAtDate: Number(sourceDeliveryArray[dateIndex]?.amountAtDate || 0),
                        amountFreeAtDate: Math.min(Number(sourceDeliveryArray[dateIndex]?.amountFreeAtDate || 0), freeAmountMax),
                    },
                    {emitEvent: false},
                );
            }
        }

        // valueChanges abonnieren und sofort auf 0..max. kostenlose Menge des Produkts kappen
        targetDeliveryArray.controls.forEach((ctrl) => {
            const fg = ctrl as UntypedFormGroup;
            const amountCtrl = fg.get('amountFreeAtDate');
            if (amountCtrl) {
                amountCtrl.valueChanges.subscribe((v) => {
                    if (v > freeAmountMax) amountCtrl.setValue(freeAmountMax, {emitEvent: false});
                    if (v < 0) amountCtrl.setValue(0, {emitEvent: false});
                });
            }
        });

        /*
         * Sekundärrabatt-Felder ggf. deaktivieren, wenn nicht erlaubt
         */
        const shouldEnableSecDisc = this.isSecondaryDiscountEnabled(productId, orderRange);
        if (!shouldEnableSecDisc) {
            duplicateGroup.get('secondary_discount')?.disable({emitEvent: false});
            duplicateGroup.get('reason_secondary_discount')?.disable({emitEvent: false});
        }

        /*
         * Grund K-Menge deaktivieren, wenn kostenlose Menge nicht erlaubt
         */
        if (!this.isFreeAmountEnabled(productId, orderRange)) {
            duplicateGroup.get('reason_amount_free')?.disable({emitEvent: false});
        }

        // Duplizierte FormGroup zurückgeben
        return duplicateGroup;
    }

    /**
     * Ermittelt den letzten Index im FormArray
     * der zum selben Produkt- und Empfänger-Paar (product_id, delivery_institution_id) gehört
     *
     * Wird verwendet, um neue Duplikate direkt nach der letzten bestehenden Zeile dieses Produkts einzufügen
     *
     * @param productId ID des Produkts
     * @param recipientId ID des Empfängers (delivery_institution_id)
     * @returns Index der letzten passenden Zeile oder -1, wenn keine gefunden wurde
     */
    private findLastSiblingIndex(productId: number, recipientId: number): number {
        const productsFormArray = this.orderItemFormGroup.get('productsFormArray') as UntypedFormArray | null;
        if (!productsFormArray) return -1;

        const productControls = productsFormArray.controls as UntypedFormGroup[];
        let lastMatchingIndex = -1;

        // Durchläuft alle Zeilen und merkt sich den Index der letzten passenden Zeile
        for (let i = 0; i < productControls.length; i += 1) {
            const formGroup = productControls[i];
            const currentProductId = Number(formGroup.get('product_id')?.value);
            const currentRecipientId = Number(formGroup.get('order_item_delivery_institution_id')?.value);

            if (currentProductId === productId && currentRecipientId === recipientId) {
                lastMatchingIndex = i;
            }
        }

        return lastMatchingIndex;
    }

    /**
     * Erstellt ein Duplikat (Kopie) für das Produkt an der angegebenen Position
     * und fügt sie direkt nach der letzten vorhandenen Zeile desselben Produkts ein
     *
     * Ablauf:
     *  - Liest die Produkt- und Empfänger-ID aus der Quellzeile
     *  - Ermittelt die nächste freie duplicate_position für dieses Produkt + Empfänger
     *  - Erstellt ein neues FormGroup-Duplikat
     *  - Fügt das Duplikat hinter die letzte bestehende Zeile ein
     *  - Subscriptions für Mengen und Preisänderungen aktivieren
     *
     * @param index Index der zu duplizierenden Zeile im FormArray
     */
    public duplicateOrderItem(index: number): void {
        const productsFormArray = this.orderItemFormGroup.get('productsFormArray') as UntypedFormArray;
        if (!productsFormArray || index < 0 || index >= productsFormArray.length) return;

        const sourceRow = productsFormArray.at(index) as UntypedFormGroup;
        const productId = Number(sourceRow.get('product_id')?.value);
        const recipientId = Number(sourceRow.get('order_item_delivery_institution_id')?.value);

        // Nächste freie Position für das Duplikat berechnen
        const nextDuplicatePosition = this.getNextDuplicatePosition(productId, recipientId);

        // Neues Duplikat aus der Quellzeile erzeugen
        const duplicateRow = this.buildDuplicateFromSource(sourceRow, nextDuplicatePosition);
        duplicateRow.get('is_duplicate')?.setValue(true, {emitEvent: false});
        duplicateRow.get('duplicate_position')?.setValue(nextDuplicatePosition, {emitEvent: false});

        // Ans Ende anfügen
        const lastSiblingIndex = this.findLastSiblingIndex(productId, recipientId);
        const insertAtIndex = lastSiblingIndex >= 0 ? lastSiblingIndex + 1 : index + 1;

        // Duplikat in das FormArray einfügen
        productsFormArray.insert(insertAtIndex, duplicateRow);

        // Subscriptions aktivieren
        this.attachProductSubscriptions(duplicateRow);

        // Rabatte neu berechnen
        this.overwriteDiscounts(insertAtIndex);
    }

    /**
     * Entfernt eine duplizierte Auftragsposition aus dem Formular
     * - Entfernt alle zugehörigen Subscriptions
     * - Entfernt die Zeile selbst aus dem FormArray
     * - Berechnet anschließend die Rbatte betroffener Gruppen neu
     *
     * @param index Index der zu entfernenden Zeile im FormArray
     */
    public removeDuplicatedOrderItem(index: number): void {
        const productsFormArray = this.orderItemFormGroup.get('productsFormArray') as UntypedFormArray;
        if (!productsFormArray || index < 0 || index >= productsFormArray.length) return;

        const productFormGroup = productsFormArray.at(index) as UntypedFormGroup;

        // Abbruch, wenn es kein Duplikat ist
        const isDuplicate = productFormGroup.get('is_duplicate')?.value === true;
        if (!isDuplicate) return;

        const productId = Number(productFormGroup.get('product_id')?.value);
        const relatedGroupIds = this.getGroupIdsForProduct(productId);

        // Subscriptions, die mit dieser Produktzeile verknüpft sind, entfernen
        const productRowSubscription = this.productRowSubscriptions.get(productFormGroup);
        productRowSubscription?.unsubscribe();
        this.productRowSubscriptions.delete(productFormGroup);

        // Aus dem FormArray entfernen
        productsFormArray.removeAt(index);

        // Rabatte der betroffenen Rabattgruppen neu berechnen
        this.recomputeForGroups(relatedGroupIds);
    }

    /**
     * Ermittelt die nächste freie duplicate_position für ein Produkt und einen Empfänger
     *
     * Beispiel:
     *  - Original (von dem dupliziert wird): duplicate_position = 0
     *  - Erstes Duplikat: duplicate_position = 1
     *  - Zweites Duplikat: duplicate_position = 2
     *
     * @param productId ID des Produkts
     * @param recipientId ID des Empfängers (delivery_institution_id)
     * @returns Nächster freier Wert für duplicate_position
     */
    private getNextDuplicatePosition(productId: number, recipientId: number): number {
        const productsFormArray = this.orderItemFormGroup.get('productsFormArray') as UntypedFormArray | null;
        if (!productsFormArray) return 1;

        const productControls = productsFormArray.controls as UntypedFormGroup[];

        // Sammle alle vorhandenen duplicate_position Werte für das selbe Produkt + Empfänger
        const existingPositions = productControls
            .filter(
                (formGroup) =>
                    Number(formGroup.get('product_id')?.value) === productId &&
                    Number(formGroup.get('order_item_delivery_institution_id')?.value) === recipientId,
            )
            .map((formGroup) => Number(formGroup.get('duplicate_position')?.value ?? 0));

        // Ermittle den höchsten vorhandenen duplicate_position Wert
        const highestExistingPosition = existingPositions.length ? Math.max(...existingPositions) : 0;

        // Nächsten freien Wert zurückgeben
        return highestExistingPosition + 1;
    }

    /**
     * Löscht alle Eingaben einer Positionszeile
     * @param index Index des Produkts
     */
    public clearOrderItemRow(index: number): void {
        const productsFormArray = this.orderItemFormGroup.get('productsFormArray') as UntypedFormArray | null;

        // Abbruch, wenn die Position nicht vorhanden ist
        if (!productsFormArray || index < 0 || index >= productsFormArray.length) return;

        const rowGroup = productsFormArray.at(index) as UntypedFormGroup;
        const amountPerDate = rowGroup.get('amountPerDeliveryDate') as UntypedFormArray | null;

        // Für jedes Lieferdatum die Mengen auf 0 setzen
        amountPerDate?.controls.forEach((control) => {
            const dateGroup = control as UntypedFormGroup;
            dateGroup.get('amountAtDate')?.setValue(0);
            dateGroup.get('amountFreeAtDate')?.setValue(0);
        });

        // Restliche Werte zurücksetzen
        rowGroup.patchValue({
            amount: 0,
            amount_free: 0,
            discount: this.orderDataCopy?.order_discount ?? 0,
            secondary_discount: null,
            reason_secondary_discount: null,
            reason_amount_free: null,
            special_price: null,
            flat_discount: null,
            charge: null,
            total_price: 0,
            valuta_date: null,
        });

        // Rabatte aktualisieren
        this.overwriteDiscounts(index);
    }

    /**
     * Prüft, ob für die Zeile Mengen (amount / amount_free) vorhanden sind
     */
    public hasOrderItemContent(index: number): boolean {
        const productsFormArray = this.orderItemFormGroup.get('productsFormArray') as UntypedFormArray | null;
        if (!productsFormArray || index < 0 || index >= productsFormArray.length) return false;

        const rowGroup = productsFormArray.at(index) as UntypedFormGroup;
        const amount = Number(rowGroup.get('amount')?.value ?? 0);
        const amountFree = Number(rowGroup.get('amount_free')?.value ?? 0);

        return amount > 0 || amountFree > 0;
    }
}
